import { IntlShape } from 'react-intl';

import type { PartnerDocType } from '@/modules/partner/model/partner-doc-type';
import {
  getRequiredDocsTypes,
  hasAllRequiredDocs,
  getUploadedRequiredDocTypes,
} from '@/shared/utils/hasAllRequiredDocs';

import type { ICard, ICardContext, TCardStatus } from '../types';

const createPartnerCards = (
  context: ICardContext,
  onNavigate: (uri: string) => void,
  intl: IntlShape,
): ICard[] => {
  const uploads = context.partner.docUploads;
  const definitions = context.partnerUploadDefinitions;

  const docsTotalAmount = definitions.length;

  if (docsTotalAmount === 0) {
    return [];
  }

  const requiredDocTypes = getRequiredDocsTypes<PartnerDocType>(definitions);
  const uploadedDocTypes = Object.keys(uploads) as PartnerDocType[];
  const uploadedDocsAmount = uploadedDocTypes.length;
  const partnerHasAllRequiredDocs = hasAllRequiredDocs<PartnerDocType>(uploads, definitions);

  let listOfRequiredDocsToUpload = definitions
    .filter(definition => !uploads[definition.docType])
    .map(doc => doc.title);

  if (listOfRequiredDocsToUpload.length > 2) {
    listOfRequiredDocsToUpload = listOfRequiredDocsToUpload.slice(0, 2);
    listOfRequiredDocsToUpload.push(
      intl.formatMessage({
        defaultMessage: 'other company documents',
        id: 'Wjf89K',
        description: 'ToDo partner card: description text part 1',
      }),
    );
  }

  const uploadedRequiredDocTypes = getUploadedRequiredDocTypes<PartnerDocType>(
    uploadedDocTypes,
    requiredDocTypes,
  );

  const cardStatus = ((): TCardStatus => {
    if (
      partnerHasAllRequiredDocs &&
      uploadedRequiredDocTypes.every(docType => uploads[docType]?.status === 'approved')
    ) {
      return 'done';
    } else if (uploadedRequiredDocTypes.some(docType => uploads[docType]?.status === 'rejected')) {
      return 'rejected';
    } else if (uploadedRequiredDocTypes.some(docType => uploads[docType]?.status === 'pending')) {
      return 'review';
    } else {
      return 'todo';
    }
  })();

  return [
    {
      action: () => onNavigate('/profile'),
      title: (() => {
        if (cardStatus === 'done') {
          return intl.formatMessage({
            defaultMessage: 'Legal information approved',
            id: 'w2MeeJ',
            description: 'ToDo partner card: title',
          });
        }

        if (cardStatus === 'review') {
          return intl.formatMessage({
            defaultMessage: 'Legal information',
            id: 'y1AtC1',
            description: 'ToDo partner card: title',
          });
        }

        return intl.formatMessage({
          defaultMessage: 'Add legal information',
          id: 'tMm4Jh',
          description: 'ToDo partner card: title',
        });
      })(),
      cardStatus,
      desc: (
        <>
          {cardStatus === 'review'
            ? intl.formatMessage({
                defaultMessage: 'Documents are in review',
                id: '7HrhYK',
                description: 'ToDo partner card: text',
              })
            : intl.formatList(listOfRequiredDocsToUpload, { type: 'conjunction' }) + '.'}

          <br />
          <br />
          {intl.formatMessage(
            {
              defaultMessage: 'Added {added} out of {total}',
              id: 'iMaQow',
              description: 'ToDo partner card: description text part 2',
            },
            {
              added: uploadedDocsAmount,
              total: docsTotalAmount,
            },
          )}
        </>
      ),
      id: 'company',
      actionTitle: intl.formatMessage({
        defaultMessage: 'add',
        id: 'lgP+Cj',
        description: 'ToDo partner card: action button',
      }),
    },
  ];
};

export { createPartnerCards };
